import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, setLanguageState] = useState(() => {
    // Check URL param first, then localStorage, then default to 'de'
    const urlLang = searchParams.get('lang');
    if (urlLang === 'en' || urlLang === 'de') return urlLang;
    
    const storedLang = localStorage.getItem('preferredLanguage');
    if (storedLang === 'en' || storedLang === 'de') return storedLang;
    
    return 'de';
  });

  const setLanguage = useCallback((newLang) => {
    if (newLang !== 'de' && newLang !== 'en') return;
    
    setLanguageState(newLang);
    localStorage.setItem('preferredLanguage', newLang);
    
    // Update URL param
    const newParams = new URLSearchParams(searchParams);
    if (newLang === 'de') {
      newParams.delete('lang'); // German is default, no need for param
    } else {
      newParams.set('lang', newLang);
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync with URL changes
  useEffect(() => {
    const urlLang = searchParams.get('lang');
    if (urlLang === 'en' && language !== 'en') {
      setLanguageState('en');
      localStorage.setItem('preferredLanguage', 'en');
    } else if (!urlLang && language !== 'de') {
      // Only reset to German if explicitly navigating without lang param
    }
  }, [searchParams, language]);

  const t = useCallback((key, translationObj) => {
    if (!translationObj) return key;
    
    // If translationObj has the key, access it first
    const translation = translationObj[key];
    if (translation && typeof translation === 'object') {
      return translation[language] || translation['de'] || key;
    }
    
    // Otherwise, assume translationObj is the actual translation object
    if (translationObj[language]) {
      return translationObj[language];
    }
    if (translationObj['de']) {
      return translationObj['de'];
    }
    
    return key;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    isGerman: language === 'de',
    isEnglish: language === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
